import { useCallback, useState } from 'react';
import { ConfirmationModal, useModalControl } from '@frontend/design-system';

type UseDeleteVoicemailConfirmationOptions<T> = {
  title: string;
  message: string;
  onConfirm: (candidate: T) => void;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

export const useDeleteVoicemailConfirmation = <T,>({
  title,
  message,
  onConfirm,
  confirmLabel,
  cancelLabel,
  destructive = true,
}: UseDeleteVoicemailConfirmationOptions<T>) => {
  const deleteModalControl = useModalControl();
  const [candidate, setCandidate] = useState<T | null>(null);

  const openDeleteModal = useCallback(
    (nextCandidate: T) => {
      setCandidate(nextCandidate);
      deleteModalControl.openModal();
    },
    [deleteModalControl]
  );

  const confirmDelete = useCallback(() => {
    if (!candidate) return;
    onConfirm(candidate);
    setCandidate(null);
  }, [candidate, onConfirm]);

  const confirmationModal = (
    <ConfirmationModal
      {...deleteModalControl.modalProps}
      title={title}
      message={message}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      destructive={destructive}
      onConfirm={confirmDelete}
    />
  );

  return {
    openDeleteModal,
    confirmationModal,
  };
};
